import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Bookmark, Plus, Trash2, ExternalLink, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

interface Bookmark {
  id: string;
  item_type: string;
  item_id: string;
  item_title: string;
  notes?: string;
  created_at: string;
}

const DashboardBookmarks = () => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newBookmark, setNewBookmark] = useState({
    item_type: "resource",
    item_title: "",
    notes: "",
  });

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("bookmarks")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load bookmarks");
      console.error(error);
    } else {
      setBookmarks(data || []);
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("bookmarks")
      .insert([{
        user_id: user.id,
        item_type: newBookmark.item_type,
        item_id: crypto.randomUUID(),
        item_title: newBookmark.item_title,
        notes: newBookmark.notes,
      }]);

    if (error) {
      toast.error("Failed to add bookmark");
      console.error(error);
    } else {
      toast.success("Bookmark added!");
      setIsDialogOpen(false);
      setNewBookmark({ item_type: "resource", item_title: "", notes: "" });
      loadBookmarks();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("bookmarks")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete bookmark");
    } else {
      toast.success("Bookmark deleted");
      loadBookmarks();
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      resource: "bg-blue-500",
      template: "bg-green-500",
      lesson: "bg-purple-500",
      blog_post: "bg-orange-500",
    };
    return colors[type] || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <Link to="/dashboard">
        <Button variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bookmark className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">My Bookmarks</h1>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Bookmark
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Bookmark</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newBookmark.item_title}
                  onChange={(e) => setNewBookmark({ ...newBookmark, item_title: e.target.value })}
                  placeholder="Enter title"
                />
              </div>
              <div>
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  className="w-full p-2 border rounded-md"
                  value={newBookmark.item_type}
                  onChange={(e) => setNewBookmark({ ...newBookmark, item_type: e.target.value })}
                >
                  <option value="resource">Resource</option>
                  <option value="template">Template</option>
                  <option value="lesson">Lesson</option>
                  <option value="blog_post">Blog Post</option>
                </select>
              </div>
              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={newBookmark.notes}
                  onChange={(e) => setNewBookmark({ ...newBookmark, notes: e.target.value })}
                  placeholder="Add notes..."
                  rows={3}
                />
              </div>
              <Button onClick={handleAdd} className="w-full">
                Add Bookmark
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {bookmarks.map((bookmark) => (
          <Card key={bookmark.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{bookmark.item_title}</CardTitle>
                  <Badge className={`mt-2 ${getTypeColor(bookmark.item_type)}`}>
                    {bookmark.item_type.replace('_', ' ')}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(bookmark.id)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bookmark.notes && (
                <p className="text-sm text-muted-foreground mb-2">{bookmark.notes}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Saved {new Date(bookmark.created_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        ))}

        {bookmarks.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <Bookmark className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No bookmarks yet. Start saving your favorite items!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardBookmarks;
